import React, { useContext, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { defineMessages, useIntl } from 'react-intl';
import SurveyItem from '../../util/SurveyItem';
import { SurveyItemComponent } from '../SurveyItem';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import {
  SurveyContext,
  SurveyResponseContext,
  IsReadOnlyContext,
  RepeatingGroupActionsContext,
  RepeatingGroupInstanceContext,
} from '../Contexts';
import { buildInstanceSegment } from '../../util/SurveyResponse';
import { getTestProps } from '../../../../util/testProps';
import useIsMobile from '../../../../hooks/useIsMobile';

const messages = defineMessages({
  addAnother: {
    id: 'surveys.repeatingGroup.addAnother',
    defaultMessage: 'Add another: {groupLabel}',
  },
  remove: {
    id: 'surveys.repeatingGroup.remove',
    defaultMessage: 'Remove',
  },
  removeConfirmTitle: {
    id: 'surveys.repeatingGroup.removeConfirmTitle',
    defaultMessage: 'Remove {instanceLabel}?',
  },
  removeConfirmContent: {
    id: 'surveys.repeatingGroup.removeConfirmContent',
    defaultMessage:
      'All answers entered for {instanceLabel} will be discarded. This cannot be undone.',
  },
  cancel: {
    id: 'surveys.repeatingGroup.cancel',
    defaultMessage: 'Cancel',
  },
});

export const testIds = {
  root: (groupLink: string) => `survey-repeatingGroup-${groupLink}`,
  instance: (groupLink: string, instanceIndex: number) =>
    `survey-repeatingGroup-instance-${groupLink}-${instanceIndex}`,
  instanceLine: (groupLink: string, instanceIndex: number, lineIndex: number) =>
    `survey-repeatingGroup-line-${groupLink}-${instanceIndex}-${lineIndex}`,
  addButton: (groupLink: string) => `survey-repeatingGroup-add-${groupLink}`,
  removeButton: (groupLink: string, instanceIndex: number) =>
    `survey-repeatingGroup-remove-${groupLink}-${instanceIndex}`,
  confirmRemoveButton: (groupLink: string) =>
    `survey-repeatingGroup-confirmRemove-${groupLink}`,
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  nestedRoot: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  instanceCard: {
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(8),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  nestedInstanceCard: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
  },
  instanceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  instanceItems: {
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  formLine: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },
  formLineStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  addButtonRow: {
    marginTop: theme.spacing(2),
  },
}));

export interface RepeatingGroupOwnProps {
  surveyItem: SurveyItem;
}

export type RepeatingGroupProps = RepeatingGroupOwnProps &
  WithCheckForEnablementInjectedProps;

/**
 * Renders a repeating group (`repeats: true` on a group item): one card per
 * answered instance with an "Add another" button.  Each instance's answers
 * are stored as a separate same-linkId item in the QuestionnaireResponse.
 */
const RepeatingGroup: React.FC<RepeatingGroupProps> = ({
  surveyItem,
  isEnabled,
}) => {
  const classes = useStyles({});
  const isMobile = useIsMobile();
  const { formatMessage } = useIntl();
  const survey = useContext(SurveyContext);
  const surveyResponse = useContext(SurveyResponseContext);
  const isReadOnly = useContext(IsReadOnlyContext);
  // set when this repeating group is nested inside another repeating group's
  // instance; paths and field names then resolve within that instance
  const parentInstance = useContext(RepeatingGroupInstanceContext);
  const { onAddInstance, onRemoveInstance } = useContext(
    RepeatingGroupActionsContext,
  );
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  if (!isEnabled) return null;

  const groupLink = surveyItem.link;
  const groupLabel = surveyItem.text || groupLink;
  const parentAncestors = parentInstance?.ancestors ?? [];
  const parentIndexByGroupLink: { [link: string]: number } = {};
  parentAncestors.forEach(({ groupLink: ancestorLink, instanceIndex }) => {
    parentIndexByGroupLink[ancestorLink] = instanceIndex;
  });
  // response path to this group's container, qualified through any enclosing
  // repeating group instances (e.g. ["page", "sisters[1]", "daughters"])
  const groupPath = survey
    .getPathToItem(groupLink)
    .map(pathSegment =>
      pathSegment in parentIndexByGroupLink
        ? buildInstanceSegment(pathSegment, parentIndexByGroupLink[pathSegment])
        : pathSegment,
    );
  const parentNamePrefix = parentInstance?.namePrefix ?? '';
  // unique across nested instances (e.g. "sisters[1].daughters"); equals the
  // bare groupLink for top-level repeating groups
  const groupKey = `${parentNamePrefix}${groupLink}`;
  const isNested = parentInstance !== null;
  const answeredInstanceCount =
    surveyResponse?.getRepeatingGroupInstanceCount(groupPath) ?? 0;

  // when editing, always show at least the minimum number of instances;
  // review mode only shows what was answered
  const displayedInstanceCount = isReadOnly
    ? answeredInstanceCount
    : Math.max(answeredInstanceCount, surveyItem.minInstanceCount);

  const maxInstanceCount = surveyItem.maxInstanceCount;
  const canAddInstance =
    !isReadOnly &&
    (maxInstanceCount === null || displayedInstanceCount < maxInstanceCount);
  const canRemoveInstances =
    !isReadOnly && displayedInstanceCount > surveyItem.minInstanceCount;

  const instanceLabel = (instanceIndex: number) =>
    `${groupLabel} ${instanceIndex + 1}`;

  const handleRemoveConfirmed = () => {
    if (pendingRemoveIndex !== null) {
      onRemoveInstance(groupPath, pendingRemoveIndex);
    }
    setPendingRemoveIndex(null);
  };

  return (
    <div
      className={clsx(classes.root, isNested && classes.nestedRoot)}
      {...getTestProps(testIds.root(groupKey))}
    >
      {Array.from({ length: displayedInstanceCount }, (_, instanceIndex) => (
        <div
          key={buildInstanceSegment(groupLink, instanceIndex)}
          className={clsx(
            classes.instanceCard,
            isNested && classes.nestedInstanceCard,
          )}
          {...getTestProps(testIds.instance(groupKey, instanceIndex))}
        >
          <div className={classes.instanceHeader}>
            <Text size="subbody" weight="bold">
              {instanceLabel(instanceIndex)}
            </Text>
            {canRemoveInstances && (
              <Button
                variant="text"
                onClick={() => setPendingRemoveIndex(instanceIndex)}
                {...getTestProps(testIds.removeButton(groupKey, instanceIndex))}
              >
                {formatMessage(messages.remove)}
              </Button>
            )}
          </div>
          <div className={classes.instanceItems}>
            <RepeatingGroupInstanceContext.Provider
              value={{
                groupLink,
                instanceIndex,
                namePrefix: `${parentNamePrefix}${buildInstanceSegment(
                  groupLink,
                  instanceIndex,
                )}.`,
                ancestors: [...parentAncestors, { groupLink, instanceIndex }],
              }}
            >
              {surveyItem.subItemsGroupedByDisplayLines.map(
                (lineSurveyItems, lineIndex) => (
                  <div
                    className={clsx(
                      classes.formLine,
                      isMobile && classes.formLineStacked,
                    )}
                    key={`${
                      lineSurveyItems[0]?.link ?? lineIndex
                    }-${instanceIndex}`}
                    {...getTestProps(
                      testIds.instanceLine(groupKey, instanceIndex, lineIndex),
                    )}
                  >
                    {lineSurveyItems.filter(Boolean).map(lineSurveyItem => (
                      <SurveyItemComponent
                        key={`${lineSurveyItem.link}-${instanceIndex}`}
                        surveyItem={lineSurveyItem}
                      />
                    ))}
                  </div>
                ),
              )}
            </RepeatingGroupInstanceContext.Provider>
          </div>
        </div>
      ))}
      {canAddInstance && (
        <div className={classes.addButtonRow}>
          <Button
            variant="outlined"
            onClick={() => onAddInstance(groupPath, displayedInstanceCount)}
            {...getTestProps(testIds.addButton(groupKey))}
          >
            {formatMessage(messages.addAnother, { groupLabel })}
          </Button>
        </div>
      )}
      <Modal
        isOpen={pendingRemoveIndex !== null}
        onDismiss={() => setPendingRemoveIndex(null)}
        title={formatMessage(messages.removeConfirmTitle, {
          instanceLabel:
            pendingRemoveIndex !== null
              ? instanceLabel(pendingRemoveIndex)
              : '',
        })}
        actions={
          <>
            <Button variant="text" onClick={() => setPendingRemoveIndex(null)}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              variant="contained"
              onClick={handleRemoveConfirmed}
              {...getTestProps(testIds.confirmRemoveButton(groupKey))}
            >
              {formatMessage(messages.remove)}
            </Button>
          </>
        }
      >
        <Text size="body">
          {formatMessage(messages.removeConfirmContent, {
            instanceLabel:
              pendingRemoveIndex !== null
                ? instanceLabel(pendingRemoveIndex)
                : '',
          })}
        </Text>
      </Modal>
    </div>
  );
};

export default withCheckForEnablement(RepeatingGroup);
